import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, TrendingUp, TrendingDown, Wallet, Users, Mail, CreditCard, Check,
  Settings, MoreHorizontal, History, FileText, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';

const fmtEUR = (n) => `${(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const metodoLabel = (m) => ({ tarjeta: 'Tarjeta', transferencia: 'Transferencia', sepa: 'SEPA' }[m] || m || '—');

export default function DesviacionesTab() {
  const [data, setData] = useState({ rows: [], kpis: null });
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ solo: 'todas', metodo: 'todos', ciclo: 'todos', min_dias: 0 });
  const [modal, setModal] = useState(null); // { tipo, row }
  const [historial, setHistorial] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filtros.solo === 'positivas') params.solo_positivas = true;
    if (filtros.solo === 'negativas') params.solo_negativas = true;
    if (filtros.metodo !== 'todos') params.metodo = filtros.metodo;
    if (filtros.ciclo !== 'todos') params.ciclo = filtros.ciclo;
    if (filtros.min_dias > 0) params.min_dias = filtros.min_dias;
    try {
      const r = await api.get('/superadmin/desviaciones', { params });
      setData(r.data);
    } catch (e) {
      toast.error('Error al cargar desviaciones');
    } finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirHistorial = async (row) => {
    try {
      const r = await api.get(`/superadmin/desviaciones/${row.empresa_id}/historial`);
      setHistorial({ empresa: row.empresa_nombre, eventos: r.data });
    } catch { toast.error('Error al cargar historial'); }
  };

  const descargarProforma = async (row) => {
    try {
      const r = await api.get(`/superadmin/desviaciones/${row.empresa_id}/proforma.pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `proforma_${row.empresa_nombre}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="desviaciones-tab">
      <KPIs kpis={data.kpis} onOpenConfig={() => setConfigOpen(true)} />

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-base">Desviaciones activas</CardTitle>
              <CardDescription>Empresas cuyo nº de usuarios activos difiere del contratado. Stripe mensual se excluye (se auto-regula).</CardDescription>
            </div>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3">
            <div className="min-w-40">
              <Label className="text-xs">Tipo desviación</Label>
              <Select value={filtros.solo} onValueChange={v => setFiltros({ ...filtros, solo: v })}>
                <SelectTrigger data-testid="filtro-solo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="positivas">Sólo positivas (a cobrar)</SelectItem>
                  <SelectItem value="negativas">Sólo negativas (crédito)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-40">
              <Label className="text-xs">Método</Label>
              <Select value={filtros.metodo} onValueChange={v => setFiltros({ ...filtros, metodo: v })}>
                <SelectTrigger data-testid="filtro-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="sepa">SEPA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-40">
              <Label className="text-xs">Ciclo</Label>
              <Select value={filtros.ciclo} onValueChange={v => setFiltros({ ...filtros, ciclo: v })}>
                <SelectTrigger data-testid="filtro-ciclo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="anual">Anual</SelectItem>
                  <SelectItem value="mensual">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-32">
              <Label className="text-xs">Min. días</Label>
              <Input type="number" value={filtros.min_dias} onChange={e => setFiltros({ ...filtros, min_dias: parseInt(e.target.value) || 0 })} data-testid="filtro-dias" />
            </div>
            <div className="ml-auto flex items-end">
              <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="abrir-config-desv">
                <Settings className="h-4 w-4 mr-1" /> Configuración
              </Button>
            </div>
          </div>

          {/* Tabla */}
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-zinc-500 bg-zinc-50">
                <tr>
                  <th className="text-left py-2 px-3">Empresa</th>
                  <th className="text-left py-2 px-3">Plan</th>
                  <th className="text-center py-2 px-3">Contratados</th>
                  <th className="text-center py-2 px-3">Activos</th>
                  <th className="text-center py-2 px-3">Δ</th>
                  <th className="text-right py-2 px-3">€ a cobrar</th>
                  <th className="text-center py-2 px-3">Días</th>
                  <th className="text-right py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.rows.map(r => (
                  <DesviacionRow key={r.empresa_id} row={r}
                    onOpenModal={(tipo) => setModal({ tipo, row: r })}
                    onOpenHistorial={() => abrirHistorial(r)}
                    onProforma={() => descargarProforma(r)}
                  />
                ))}
                {data.rows.length === 0 && !loading && (
                  <tr><td colSpan={8} className="py-10 text-center text-zinc-400">🎉 No hay empresas con desviación de asientos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modales */}
      <CobrarTarjetaDialog open={modal?.tipo === 'tarjeta'} row={modal?.row} onClose={() => setModal(null)} onOk={() => { setModal(null); cargar(); }} />
      <RegularizarManualDialog open={modal?.tipo === 'manual'} row={modal?.row} onClose={() => setModal(null)} onOk={() => { setModal(null); cargar(); }} />
      <AjustarPlanDialog open={modal?.tipo === 'ajustar'} row={modal?.row} onClose={() => setModal(null)} onOk={() => { setModal(null); cargar(); }} />
      <ContactarDialog open={modal?.tipo === 'contactar'} row={modal?.row} onClose={() => setModal(null)} onOk={() => { setModal(null); cargar(); }} />
      <HistorialDialog data={historial} onClose={() => setHistorial(null)} />
      <ConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} onSaved={() => { setConfigOpen(false); cargar(); }} />
    </div>
  );
}

function KPIs({ kpis, onOpenConfig }) {
  if (!kpis) {
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => <Card key={i}><CardContent className="p-4 h-20 animate-pulse bg-zinc-100" /></Card>)}
    </div>;
  }
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KPI icon={AlertTriangle} color="amber" label="Empresas con desviación" value={kpis.total_empresas} sub={`${kpis.positivas} a cobrar · ${kpis.negativas} crédito`} testid="kpi-total" />
      <KPI icon={Wallet} color="red" label="Pendiente de cobro" value={fmtEUR(kpis.pendiente_cobro)} sub="Asientos extra sin regularizar" testid="kpi-pendiente" />
      <KPI icon={History} color="blue" label="Media días con desviación" value={kpis.media_dias} sub="Antigüedad promedio" testid="kpi-media" />
      <Card className="bg-zinc-50 border-zinc-200 cursor-pointer hover:bg-zinc-100" onClick={onOpenConfig} data-testid="kpi-config">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide opacity-70">Configuración</p>
            <p className="text-sm mt-1 font-medium">Umbrales y grace</p>
            <p className="text-xs mt-1 opacity-60">Aviso admin, aviso super, bloqueo suave</p>
          </div>
          <Settings className="h-5 w-5 opacity-60" />
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ icon: Icon, color, label, value, sub, testid }) {
  const bg = {
    red: 'bg-red-50 border-red-200 text-red-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  }[color];
  return (
    <Card className={bg} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
          </div>
          <Icon className="h-5 w-5 opacity-60" />
        </div>
      </CardContent>
    </Card>
  );
}

function DesviacionRow({ row, onOpenModal, onOpenHistorial, onProforma }) {
  const positiva = row.delta > 0;
  return (
    <tr className="hover:bg-zinc-50" data-testid={`desv-row-${row.empresa_id}`}>
      <td className="py-2 px-3 font-medium">{row.empresa_nombre}</td>
      <td className="py-2 px-3 text-xs">
        <div className="flex flex-col gap-0.5">
          <Badge variant="outline" className="text-[10px] w-fit">{row.ciclo}</Badge>
          <Badge variant="outline" className="text-[10px] w-fit">{metodoLabel(row.metodo_pago)}</Badge>
        </div>
      </td>
      <td className="py-2 px-3 text-center tabular-nums">{row.contratados}</td>
      <td className="py-2 px-3 text-center tabular-nums font-semibold">{row.activos}</td>
      <td className="py-2 px-3 text-center">
        <Badge variant="outline" className={`tabular-nums ${positiva ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
          {positiva ? <ArrowUpRight className="h-3 w-3 inline mr-0.5" /> : <ArrowDownRight className="h-3 w-3 inline mr-0.5" />}
          {row.delta > 0 ? `+${row.delta}` : row.delta} ({row.pct}%)
        </Badge>
      </td>
      <td className="py-2 px-3 text-right tabular-nums">
        {positiva ? <span className="font-semibold text-red-700">{fmtEUR(row.importe_pendiente)}</span> : <span className="text-zinc-400">—</span>}
      </td>
      <td className="py-2 px-3 text-center text-xs text-zinc-500 tabular-nums">{row.dias_con_desviacion}</td>
      <td className="py-2 px-3">
        <div className="flex items-center justify-end gap-1">
          {positiva && (
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => onOpenModal('tarjeta')} data-testid={`btn-tarjeta-${row.empresa_id}`}>
              <CreditCard className="h-3.5 w-3.5 mr-1" /> Cobrar
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`menu-${row.empresa_id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {positiva && (
                <DropdownMenuItem onClick={() => onOpenModal('manual')} data-testid={`op-manual-${row.empresa_id}`}>
                  <Check className="h-4 w-4 mr-2 text-emerald-600" /> Regularizar manualmente
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onOpenModal('ajustar')} data-testid={`op-ajustar-${row.empresa_id}`}>
                <Users className="h-4 w-4 mr-2" /> Ajustar plan sin cobrar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onOpenModal('contactar')} data-testid={`op-contactar-${row.empresa_id}`}>
                <Mail className="h-4 w-4 mr-2" /> Contactar cliente por email
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {positiva && (
                <DropdownMenuItem onClick={onProforma}>
                  <FileText className="h-4 w-4 mr-2" /> Descargar proforma PDF
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onOpenHistorial}>
                <History className="h-4 w-4 mr-2" /> Historial regularizaciones
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

function CobrarTarjetaDialog({ open, row, onClose, onOk }) {
  const [nota, setNota] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  useEffect(() => { if (open) { setNota(''); setCheckoutUrl(''); } }, [open]);
  if (!row) return null;
  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/superadmin/desviaciones/${row.empresa_id}/cobrar-tarjeta`, { nota_interna: nota });
      setCheckoutUrl(r.data.checkout_url);
      toast.success('Enlace de cobro generado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cobrar {row.delta} asiento(s) extra — {row.empresa_nombre}</DialogTitle>
          <DialogDescription>
            Importe: <b className="text-red-700">{fmtEUR(row.importe_pendiente)}</b> (IVA incluido).
            Se creará una sesión de Stripe Checkout one-off y una factura pendiente.
            Al confirmarse el pago (webhook), la empresa quedará regularizada automáticamente.
          </DialogDescription>
        </DialogHeader>
        {!checkoutUrl ? (
          <>
            <div>
              <Label>Nota interna (opcional)</Label>
              <Textarea value={nota} onChange={e => setNota(e.target.value)} placeholder="Contexto o justificación..." data-testid="nota-tarjeta" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirmar-tarjeta">
                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1" />}
                Generar enlace Stripe
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-3">
            <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-800">
              ✅ Enlace de pago generado. Envíalo al cliente:
            </div>
            <div className="flex gap-2">
              <Input value={checkoutUrl} readOnly className="text-xs" data-testid="checkout-url" />
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(checkoutUrl); toast.success('Copiado'); }}>Copiar</Button>
            </div>
            <Button asChild variant="secondary" className="w-full">
              <a href={checkoutUrl} target="_blank" rel="noreferrer">Abrir Stripe Checkout</a>
            </Button>
            <DialogFooter>
              <Button variant="outline" onClick={() => { onOk(); }}>Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RegularizarManualDialog({ open, row, onClose, onOk }) {
  const [motivo, setMotivo] = useState('transferencia');
  const [ref, setRef] = useState('');
  const [nota, setNota] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setMotivo('transferencia'); setRef(''); setNota(''); } }, [open]);
  if (!row) return null;
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/superadmin/desviaciones/${row.empresa_id}/regularizar-manual`, { motivo, referencia_bancaria: ref, nota_interna: nota });
      toast.success(motivo === 'cortesia' ? 'Regularizado sin cobro' : 'Regularizado y factura emitida');
      onOk();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Regularizar manualmente — {row.empresa_nombre}</DialogTitle>
          <DialogDescription>
            Importe: <b>{fmtEUR(row.importe_pendiente)}</b>. Se emitirá una factura interna con IVA en estado pagada
            (salvo &quot;cortesía&quot;), se actualizará <b>max_empleados</b> a {row.activos} y se guardará el evento.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Motivo *</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger data-testid="motivo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transferencia">Transferencia recibida</SelectItem>
                <SelectItem value="sepa">SEPA confirmada</SelectItem>
                <SelectItem value="cortesia">Cortesía comercial (sin cobro)</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(motivo === 'transferencia' || motivo === 'sepa') && (
            <div>
              <Label>Referencia bancaria (opcional)</Label>
              <Input value={ref} onChange={e => setRef(e.target.value)} placeholder="Ej: TRF20260724-001" data-testid="ref-manual" />
            </div>
          )}
          <div>
            <Label>Nota interna (opcional)</Label>
            <Textarea value={nota} onChange={e => setNota(e.target.value)} placeholder="Comentario para el histórico..." data-testid="nota-manual" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirmar-manual">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Regularizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AjustarPlanDialog({ open, row, onClose, onOk }) {
  const [nuevo, setNuevo] = useState(0);
  const [motivo, setMotivo] = useState('');
  useEffect(() => { if (open && row) { setNuevo(row.activos); setMotivo(''); } }, [open, row]);
  if (!row) return null;
  const submit = async () => {
    try {
      await api.post(`/superadmin/desviaciones/${row.empresa_id}/ajustar-plan`, { nuevo_max_empleados: nuevo, motivo });
      toast.success(`Plan ajustado a ${nuevo} asientos`);
      onOk();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajustar plan — {row.empresa_nombre}</DialogTitle>
          <DialogDescription>
            Actual: {row.contratados} asientos contratados, {row.activos} activos.
            Cambia el baseline sin cobrar (útil cuando se acuerda un nuevo plan por acuerdo comercial).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nuevo máximo de asientos</Label>
            <Input type="number" min="1" value={nuevo} onChange={e => setNuevo(parseInt(e.target.value) || 1)} data-testid="ajustar-nuevo" />
          </div>
          <div>
            <Label>Motivo (opcional)</Label>
            <Textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Acuerdo comercial, downgrade, upgrade..." data-testid="ajustar-motivo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} data-testid="confirmar-ajustar">Ajustar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactarDialog({ open, row, onClose, onOk }) {
  const [loading, setLoading] = useState(false);
  if (!row) return null;
  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/superadmin/desviaciones/${row.empresa_id}/contactar`);
      toast.success(`Email enviado a ${r.data.email}`);
      onOk();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contactar por email — {row.empresa_nombre}</DialogTitle>
          <DialogDescription>
            Se enviará un email al administrador de la empresa con el desglose de la desviación
            ({row.delta > 0 ? `${row.delta} asientos extra por ${fmtEUR(row.importe_pendiente)}` : `${Math.abs(row.delta)} asientos por debajo del plan`}).
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="confirmar-contactar">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
            Enviar email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistorialDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historial de regularizaciones — {data.empresa}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {(data.eventos || []).map(e => (
            <Card key={e.id} className="border-zinc-200">
              <CardContent className="p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <Badge variant="outline" className="text-xs">{e.tipo.replace(/_/g, ' ')}</Badge>
                  <span className="text-xs text-zinc-500">{(e.at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
                {e.motivo && <div><b>Motivo:</b> {e.motivo}</div>}
                {typeof e.delta === 'number' && <div><b>Δ asientos:</b> {e.delta > 0 ? `+${e.delta}` : e.delta}</div>}
                {e.nuevo_max_empleados && <div><b>Nuevo max:</b> {e.nuevo_max_empleados}</div>}
                {typeof e.importe === 'number' && e.importe > 0 && <div><b>Importe:</b> {fmtEUR(e.importe)}</div>}
                {e.referencia_bancaria && <div><b>Ref. bancaria:</b> {e.referencia_bancaria}</div>}
                {e.nota && <div className="text-zinc-600 text-xs mt-1">{e.nota}</div>}
              </CardContent>
            </Card>
          ))}
          {(data.eventos || []).length === 0 && <p className="text-sm text-zinc-500 text-center py-6">Sin eventos previos.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigDialog({ open, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open) api.get('/superadmin/desviaciones/config').then(r => setCfg(r.data)).catch(() => {});
  }, [open]);
  if (!cfg) return null;
  const guardar = async () => {
    setLoading(true);
    try {
      await api.post('/superadmin/desviaciones/config', cfg);
      toast.success('Configuración guardada');
      onSaved();
    } catch { toast.error('Error al guardar'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configuración de desviaciones</DialogTitle>
          <DialogDescription>Umbrales de avisos y modo bloqueo suave. Cambios se aplican inmediatamente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Aviso al admin (días)</Label>
              <Input type="number" min="1" value={cfg.aviso_admin_dias} onChange={e => setCfg({ ...cfg, aviso_admin_dias: parseInt(e.target.value) || 1 })} data-testid="cfg-aviso-admin" />
              <p className="text-xs text-zinc-500 mt-1">Días con desviación &gt;0 antes de avisar al admin</p>
            </div>
            <div>
              <Label>Aviso al superadmin (días)</Label>
              <Input type="number" min="1" value={cfg.aviso_superadmin_dias} onChange={e => setCfg({ ...cfg, aviso_superadmin_dias: parseInt(e.target.value) || 1 })} data-testid="cfg-aviso-super" />
            </div>
          </div>
          <div>
            <Label>Umbral aviso superadmin (%)</Label>
            <Input type="number" min="0" step="1" value={cfg.aviso_superadmin_pct} onChange={e => setCfg({ ...cfg, aviso_superadmin_pct: parseFloat(e.target.value) || 0 })} data-testid="cfg-pct-super" />
            <p className="text-xs text-zinc-500 mt-1">% de desviación por encima del cual se avisa al superadmin</p>
          </div>
          <div className="border-t border-zinc-100 pt-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Bloqueo suave</Label>
                <p className="text-xs text-zinc-500">Permite crear empleados por encima del plan hasta cierto %</p>
              </div>
              <Switch checked={cfg.permitir_grace} onCheckedChange={v => setCfg({ ...cfg, permitir_grace: v })} data-testid="cfg-grace-switch" />
            </div>
            {cfg.permitir_grace && (
              <div>
                <Label>Tolerancia máxima (%)</Label>
                <Input type="number" min="0" max="200" value={cfg.grace_tolerancia_pct} onChange={e => setCfg({ ...cfg, grace_tolerancia_pct: parseFloat(e.target.value) || 0 })} data-testid="cfg-grace-pct" />
                <p className="text-xs text-zinc-500 mt-1">Ej: 20% permite superar el plan hasta un +20% antes de bloquear.</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={loading} className="bg-zinc-900" data-testid="cfg-guardar">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
